"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CategoryCard } from "@/components/CategoryCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon, Check, ChevronLeft, ChevronRight, Clock, CreditCard, Loader2, Tag, Users, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ClientOnlyWrapper } from "@/components/ClientOnlyWrapper";

import { Calendar } from "@/components/ui/calendar";
import { CustomerInfo, TierParticipantCount, ServiceWithPricing, TimeSlotInfo } from "@/lib/types";
import { useSearchParams } from "next/navigation";

import { PricingCalculator } from "@/components/PricingCalculator";
import ServiceOptionsSelector from "@/components/ServiceOptionsSelector";
import SecurityDepositSelector from "@/components/security-deposit/SecurityDepositSelector";
import SecurityDepositAuthorizationForm from "@/components/security-deposit/SecurityDepositAuthorizationForm";
import {
	OptionSelection,
	ServiceOption,
	ServiceOptionAssignment,
	RequiredOptionGroup,
} from "@/lib/types/service-options";
import { calculateOptionsPricing, validateOptionSelections } from "@/lib/service-options";
import { DepositSelection } from "@/components/payment/DepositSelection";
import { PaymentType } from "@/lib/types/payments";
import dynamicImport from "next/dynamic";
import { findParticipantRange, formatParticipantRangeLabel, getParticipantRangePrice } from "@/lib/participant-pricing";

const PaymentWrapper = dynamicImport(
	() => import("@/components/payment/PaymentWrapper").then((mod) => ({ default: mod.PaymentWrapper })),
	{
		ssr: false,
		loading: () => (
			<div className="flex items-center justify-center p-8">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
			</div>
		),
	}
);
import { formatTimeFromISO, formatTimeRangeFromISO } from "@/lib/time-utils";
import { debugLog } from "@/lib/env";

// Simplified booking item using tier participant counts
interface BookingItem {
	service: ServiceWithPricing;
	date: string;
	timeSlot: TimeSlotInfo;
	tierParticipants: TierParticipantCount[];
	totalPrice: number;
	selectedOptions: OptionSelection[];
	optionsPrice: number;
}

interface ServiceCategory {
	id: string;
	name: string;
	description: string | null;
	image_url: string | null;
	display_order: number;
	is_active: boolean;
}

type ServiceWithOptions = ServiceWithPricing & {
	required_option_groups?: RequiredOptionGroup[];
};

interface FormData extends CustomerInfo {
	specialRequests: string;
}

// Fetch real availability data from API
const fetchTimeSlots = async (
	serviceId: string,
	date: string,
	participantCount: number = 1
): Promise<TimeSlotInfo[]> => {
	try {
		const response = await fetch(
			`/api/services/${serviceId}/timeslots?date=${date}&participants=${participantCount}`
		);
		if (!response.ok) throw new Error("Failed to fetch time slots");
		const data = await response.json();

		return data.data || [];
	} catch (error) {
		console.error("Error fetching time slots:", error);
		return [];
	}
};

const steps = [
	{ id: 1, title: "Activités & Options", description: "Choisissez vos excursions et options" },
	{ id: 2, title: "Date & Heure", description: "Sélectionnez vos créneaux" },
	{ id: 3, title: "Participants", description: "Nombre de personnes" },
	{ id: 4, title: "Informations", description: "Vos coordonnées" },
	{ id: 5, title: "Confirmation", description: "Vérifiez votre réservation" },
	{ id: 6, title: "Paiement", description: "Paiement sécurisé" },
];

export default function ReservationPage() {

	const [currentStep, setCurrentStep] = useState(() => {
		if (typeof window === "undefined") return 1;
		const params = new URLSearchParams(window.location.search);
		const stepParam = params.get("step");
		if (stepParam && stepParam !== "security-deposit") {
			const numericStep = Number(stepParam);
			if (Number.isFinite(numericStep) && numericStep >= 1 && numericStep <= 5) {
				return numericStep;
			}
		}
		if (params.get("service")) return 2;
		return 1;
	});
	const [services, setServices] = useState<ServiceWithOptions[]>([]);
	const [categories, setCategories] = useState<ServiceCategory[]>([]);
	const [categoriesLoading, setCategoriesLoading] = useState(true);
	const [categoriesError, setCategoriesError] = useState<string | null>(null);
	const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
	const [selectedService, setSelectedService] = useState<string | null>(null);
	const [bookingItems, setBookingItems] = useState<BookingItem[]>([]);
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
	const [timeSlots, setTimeSlots] = useState<TimeSlotInfo[]>([]);
	const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);
	const [totalPrice, setTotalPrice] = useState(0);
	const [tierParticipants, setTierParticipants] = useState<TierParticipantCount[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedOptions, setSelectedOptions] = useState<OptionSelection[]>([]);
	const [optionsPrice, setOptionsPrice] = useState(0);
	const [servicesLoading, setServicesLoading] = useState(true);
	const [serviceAssignments, setServiceAssignments] = useState<Record<string, ServiceOptionAssignment[]>>({});
	const [serviceRequiredGroups, setServiceRequiredGroups] = useState<Record<string, RequiredOptionGroup[]>>({});
	const [showOptionsValidation, setShowOptionsValidation] = useState(false);
	const [formData, setFormData] = useState<FormData>({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		specialRequests: "",
	});
	const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
	const [reservationId, setReservationId] = useState<string | null>(null);

	const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>("full");
	const [paymentAmount, setPaymentAmount] = useState<number>(0);

	// Security deposit state
	const [securityDepositMethod, setSecurityDepositMethod] = useState<"authorization" | "manual" | null>(null);
	const [securityDepositAmount, setSecurityDepositAmount] = useState<number>(0);
	const [requiresSecurityDeposit, setRequiresSecurityDeposit] = useState<boolean>(false);
	const [securityDepositPaymentIntentId, setSecurityDepositPaymentIntentId] = useState<string | null>(null);

	// Discount coupon state
	const [discountCode, setDiscountCode] = useState<string>("");
	const [discountAmount, setDiscountAmount] = useState<number>(0);
	const [discountError, setDiscountError] = useState<string>("");
	const [redirectBookingData, setRedirectBookingData] = useState<any>(null);

	// Compute effective security deposit amount (scales with units for per_equipment_unit/per_participant_range)
	const effectiveSecurityDepositAmount = useMemo(() => {
		if (!requiresSecurityDeposit) return 0;

		const svc = services.find((s) => s.id === selectedService);

		// Determine pricing model with booking fallback
		const pricingModel =
			(svc?.pricing_model as any) || (redirectBookingData as any)?.service_pricing_model || "per_participant";

		// Base deposit per equipment unit (prefer service; fallback to booking meta)
		const base =
			(svc?.security_deposit_amount as number | undefined) ??
			((redirectBookingData as any)?.service_security_deposit_amount as number | undefined) ??
			0;

		if (pricingModel !== "per_equipment_unit" && pricingModel !== "per_participant_range") return base;

		// Determine participants with booking fallback
		const totalParticipants =
			tierParticipants && tierParticipants.length > 0
				? tierParticipants.reduce((sum, tp) => sum + (tp.count || 0), 0)
				: bookingItems.length > 0
					? bookingItems[0].tierParticipants.reduce((sum, tp) => sum + (tp.count || 0), 0)
					: ((redirectBookingData as any)?.items?.[0]?.participants ?? 1);

		// Determine unit capacity with booking fallback
		const unitCapacity =
			(svc?.equipment_requirements?.[0]?.equipment?.capacity_per_unit as number | undefined) ??
			((redirectBookingData as any)?.service_equipment_requirements?.[0]?.equipment?.capacity_per_unit as
				| number
				| undefined) ??
			1;

		const unitsUsed = Math.max(1, Math.ceil(totalParticipants / unitCapacity));
		return base * unitsUsed;
	}, [requiresSecurityDeposit, services, selectedService, tierParticipants, bookingItems, redirectBookingData]);

	const [discountLoading, setDiscountLoading] = useState<boolean>(false);
	const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

	const searchParams = useSearchParams();
	const preSelectedServiceId = searchParams.get("service");
	const preSelectedOptionsParam = searchParams.get("options")?.split(",").filter(Boolean) || [];
	const preSelectedOptions: OptionSelection[] = preSelectedOptionsParam.map((optionId) => ({
		optionId,
		quantity: 1,
	}));
	const preSelectedTierParticipants = searchParams.get("tierParticipants");
	const preSelectedTotalPrice = searchParams.get("totalPrice");
	const preSelectedOptionsPrice = searchParams.get("optionsPrice");

	// Security deposit redirect parameters
	const redirectStep = searchParams.get("step");
	const redirectBookingId = searchParams.get("booking");
	const redirectPaymentIntent = searchParams.get("payment_intent");
	const redirectServiceId = searchParams.get("service_id");
	const redirectAmount = searchParams.get("amount");
	const initialStep = useMemo(() => {
		if (!redirectStep || redirectStep === "security-deposit") return null;
		const numericStep = Number(redirectStep);
		if (!Number.isFinite(numericStep) || numericStep < 1 || numericStep > 5) return null;
		return numericStep;
	}, [redirectStep]);

	const parseTierParticipants = (value: string | null): TierParticipantCount[] => {
		if (!value) return [];
		try {
			const decodedValue = decodeURIComponent(value);
			const parsed = JSON.parse(decodedValue);
			if (!Array.isArray(parsed)) return [];
			return parsed
				.map((entry) => ({
					tierId: String((entry as any)?.tierId ?? ""),
					count: Number((entry as any)?.count ?? 0),
				}))
				.filter((entry) => entry.tierId && Number.isFinite(entry.count) && entry.count > 0);
		} catch (error) {
			console.error("Error parsing tier participant data:", error);
			return [];
		}
	};

	useEffect(() => {
		if (!preSelectedServiceId || redirectStep === "security-deposit") return;
		const targetStep = initialStep ?? 2;
		setCurrentStep((prev) => (prev < targetStep ? targetStep : prev));
		if (!selectedService) {
			setSelectedService(preSelectedServiceId);
		}
	}, [preSelectedServiceId, redirectStep, initialStep, selectedService]);

	// Fetch services from database
	useEffect(() => {
		const fetchServices = async () => {
			try {
				const response = await fetch("/api/services");
				if (!response.ok) throw new Error("Failed to fetch services");
				const data = await response.json();

				const nextServices: ServiceWithOptions[] = (data.services || []).map((service: ServiceWithOptions) => ({
					...service,
					optionsV2: service.optionsV2 || [],
					required_option_groups: service.required_option_groups || [],
				}));

				setServices(nextServices);

				const assignmentsMap: Record<string, ServiceOptionAssignment[]> = {};
				const groupsMap: Record<string, RequiredOptionGroup[]> = {};

				for (const service of nextServices) {
					assignmentsMap[service.id] = service.optionsV2 || [];
					groupsMap[service.id] = service.required_option_groups || [];
				}

				setServiceAssignments(assignmentsMap);
				setServiceRequiredGroups(groupsMap);

				// Handle entry point detection
				if (preSelectedServiceId) {
					// Coming from service detail page - pre-select service and skip to step 2
					const foundService = nextServices.find((s) => s.id === preSelectedServiceId);
					if (foundService) {
						setSelectedService(preSelectedServiceId);
						setSelectedCategoryId(foundService.category_id || null);

						// Set security deposit info
						setRequiresSecurityDeposit(foundService.requires_security_deposit || false);
						setSecurityDepositAmount(foundService.security_deposit_amount || 0);

						// Pre-select options if provided
						if (preSelectedOptions.length > 0) {
							setSelectedOptions(preSelectedOptions);
							// Also update booking items if they exist
							if (bookingItems.length > 0) {
								updateSelectedOptions(0, preSelectedOptions, 0);
							}
						}

						// Pre-populate tier participants if provided
						if (preSelectedTierParticipants) {
							const tierParticipantData = parseTierParticipants(preSelectedTierParticipants);
							if (tierParticipantData.length > 0) {
								setTierParticipants(tierParticipantData);
							}
						}

						// Pre-populate pricing if provided
						if (preSelectedTotalPrice) {
							const parsedTotalPrice = Number(preSelectedTotalPrice);
							if (Number.isFinite(parsedTotalPrice)) {
								setTotalPrice(parsedTotalPrice);
							}
						}
						if (preSelectedOptionsPrice) {
							const parsedOptionsPrice = Number(preSelectedOptionsPrice);
							if (Number.isFinite(parsedOptionsPrice)) {
								setOptionsPrice(parsedOptionsPrice);
							}
						}

						// Go to the requested step, defaulting to date/time selection
						setCurrentStep((prev) => {
							const targetStep = initialStep ?? 2;
							return prev < targetStep ? targetStep : prev;
						});
					}
				}
			} catch (error) {
				console.error("Error fetching services:", error);
			} finally {
				setServicesLoading(false);
			}
		};

		fetchServices();
	}, [preSelectedServiceId]); // Only depend on service ID to prevent infinite loops

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				setCategoriesLoading(true);
				setCategoriesError(null);
				const response = await fetch("/api/service-categories");
				if (!response.ok) {
					throw new Error("Failed to fetch categories");
				}
				const data = await response.json();
				setCategories(data.categories || []);
			} catch (error) {
				setCategoriesError(error instanceof Error ? error.message : "An error occurred");
			} finally {
				setCategoriesLoading(false);
			}
		};

		fetchCategories();
	}, []);

	// Handle security deposit redirect from confirmation page
	useEffect(() => {
		const initSecurityDepositFlow = async () => {
			if (redirectStep === "security-deposit" && redirectBookingId && redirectServiceId) {
				// Set up the security deposit flow
				setReservationId(redirectBookingId);
				setSelectedService(redirectServiceId);
				setRequiresSecurityDeposit(true);
				setCurrentStep(6.5); // Go directly to security deposit selection

				// Fetch booking meta so we have participants and unit capacity immediately
				try {
					const res = await fetch(`/api/bookings/${redirectBookingId}`);
					const json = await res.json();
					setRedirectBookingData(json?.data || null);
				} catch (e) {
					console.error("Failed to load booking meta for security deposit:", e);
				}
			}
		};
		initSecurityDepositFlow();
	}, [redirectStep, redirectBookingId, redirectServiceId]);

	// Supprimer la fonction generateDates car le composant Calendar gère la plage

	// Load time slots when date changes
	useEffect(() => {
		if (selectedDate && selectedService) {
			const loadTimeSlots = async () => {
				setTimeSlotsLoading(true);
				setSelectedTimeSlot(null); // Reset selected time slot when date changes
				try {
					// Format date properly to avoid timezone issues
					const year = selectedDate.getFullYear();
					const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
					const day = String(selectedDate.getDate()).padStart(2, "0");
					const dateString = `${year}-${month}-${day}`;

					// Always fetch slots with participants=1 to get all available slots
					// We'll handle capacity warnings on the client side
					const serviceSlots = await fetchTimeSlots(selectedService, dateString, 1);
					setTimeSlots(serviceSlots);
				} catch (error) {
					console.error("Error loading time slots:", error);
					setTimeSlots([]);
				} finally {
					setTimeSlotsLoading(false);
				}
			};

			loadTimeSlots();
		} else {
			setTimeSlots([]);
			setSelectedTimeSlot(null);
			setTimeSlotsLoading(false);
		}
	}, [selectedDate, selectedService]);

	const toggleService = (serviceId: string) => {
		console.log("=== TOGGLE SERVICE CALLED ===", serviceId);
		const newSelectedService = selectedService === serviceId ? null : serviceId;
		setSelectedService(newSelectedService);

		// Update security deposit info when service changes
		if (newSelectedService) {
			const service = services.find((s) => s.id === newSelectedService);
			if (service) {
				console.log("=== SERVICE SELECTION DEBUG ===");
				console.log("Selected service:", service.name);
				console.log("Service security deposit fields:", {
					requires_security_deposit: service.requires_security_deposit,
					security_deposit_amount: service.security_deposit_amount,
				});

				setRequiresSecurityDeposit(service.requires_security_deposit || false);
				setSecurityDepositAmount(service.security_deposit_amount || 0);

				console.log("Set requiresSecurityDeposit to:", service.requires_security_deposit || false);
				console.log("Set securityDepositAmount to:", service.security_deposit_amount || 0);
			}
		} else {
			setRequiresSecurityDeposit(false);
			setSecurityDepositAmount(0);
		}

		// Reset security deposit method when service changes
		setSecurityDepositMethod(null);
		setSecurityDepositPaymentIntentId(null);
	};

	const handleCategorySelect = (categoryId: string) => {
		setSelectedCategoryId(categoryId);
		if (selectedService) {
			const current = services.find((service) => service.id === selectedService);
			if (!current || current.category_id !== categoryId) {
				setSelectedService(null);
				setSelectedOptions([]);
				setOptionsPrice(0);
				setTierParticipants([]);
			}
		}
	};

	const handleCategoryReset = () => {
		setSelectedCategoryId(null);
		setSelectedService(null);
		setSelectedOptions([]);
		setOptionsPrice(0);
		setTierParticipants([]);
	};

	const addBookingItem = (timeSlotTime: string) => {
		if (!selectedService) return;

		const service = services.find((s) => s.id === selectedService);
		const timeSlot = timeSlots.find((slot) => {
			const slotTime = formatTimeFromISO(slot.start_time);
			return slotTime === timeSlotTime;
		});
		if (!service || !selectedDate || !timeSlot) return;

		// Calculate base price from pricing tiers - use first tier as fallback for initial display
		let basePrice = service.pricing_tiers?.[0]?.price || service.base_price || 0;
		if (service.pricing_model === "per_participant_range" && service.participant_pricing_ranges?.length) {
			basePrice = service.participant_pricing_ranges[0].total_price;
		}

		// Calculate options price using new service options system
		let currentOptionsPrice = 0;
		const assignments = serviceAssignments[service.id] || [];
		if (assignments.length > 0 && selectedOptions.length > 0) {
			// Calculate total participant count
			const totalParticipants = tierParticipants.reduce((sum, tp) => sum + tp.count, 0);
			const pricing = calculateOptionsPricing(assignments, selectedOptions, totalParticipants);
			currentOptionsPrice = pricing.total;
		}

		// Format date properly to avoid timezone issues
		const year = selectedDate.getFullYear();
		const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
		const day = String(selectedDate.getDate()).padStart(2, "0");
		const date = `${year}-${month}-${day}`;

		const time = formatTimeFromISO(timeSlot.start_time); // Use timezone-safe time formatting
		const syntheticTimeSlotId = `${selectedService}|${date}|${time}`;

		const newItem: BookingItem = {
			service,
			date,
			timeSlot: {
				...timeSlot,
				id: syntheticTimeSlotId,
			},
			tierParticipants: tierParticipants.length > 0 ? tierParticipants : [],
			totalPrice: totalPrice > 0 ? totalPrice + optionsPrice : basePrice + currentOptionsPrice,
			selectedOptions: [...selectedOptions],
			optionsPrice: optionsPrice > 0 ? optionsPrice : currentOptionsPrice,
		};

		setSelectedTimeSlot(timeSlotTime);
		setBookingItems([newItem]); // Only one booking item since we only allow one service
	};

	const updateTierParticipants = useCallback(
		(index: number, tierParticipants: TierParticipantCount[], totalPrice: number) => {
			setBookingItems((prev) =>
				prev.map((item, i) => {
					if (i === index) {
						// Calculate total participant count
						const participantCount = tierParticipants.reduce((sum, tp) => sum + tp.count, 0);

						// Recalculate options price with new participant count using new system
						let calculatedOptionsPrice = 0;
						if (item.service.optionsV2 && item.selectedOptions.length > 0) {
							const pricing = calculateOptionsPricing(
								item.service.optionsV2,
								item.selectedOptions,
								participantCount
							);
							calculatedOptionsPrice = pricing.total;
						}

						const finalTotalPrice = totalPrice + calculatedOptionsPrice;

						return {
							...item,
							tierParticipants,
							optionsPrice: calculatedOptionsPrice,
							totalPrice: finalTotalPrice,
						};
					}
					return item;
				})
			);
		},
		[]
	);

	// Memoized callback for price changes to prevent infinite re-renders
	const handlePriceChange = useCallback(
		(price: number, tierParticipantList: TierParticipantCount[]) => {
			setTotalPrice(price);
			setTierParticipants(tierParticipantList);
			// Update booking item with new pricing info
			updateTierParticipants(0, tierParticipantList, price);
		},
		[updateTierParticipants]
	);

	const updateSelectedOptions = (index: number, selectedOptions: OptionSelection[], _optionsPrice: number) => {
		setBookingItems((prev) =>
			prev.map((item, i) => {
				if (i === index) {
					// Use first tier as fallback for options calculation
					let basePrice = item.service.pricing_tiers?.[0]?.price || item.service.base_price || 0;
					// Calculate total participant count
					const participantCount = item.tierParticipants.reduce((sum, tp) => sum + tp.count, 0);

					// Calculate options price using the new system
					let calculatedOptionsPrice = 0;
					if (item.service.optionsV2) {
						const pricing = calculateOptionsPricing(
							item.service.optionsV2,
							selectedOptions,
							participantCount
						);
						calculatedOptionsPrice = pricing.total;
					}

					// Handle pricing models: per_participant, fixed_total, per_equipment_unit, per_participant_range
					const priceMode = (item.service.pricing_model as any) || "per_participant";
					let totalPrice = 0;
					if (priceMode === "fixed_total") {
						totalPrice = basePrice + calculatedOptionsPrice;
					} else if (priceMode === "per_participant_range") {
						const ranges = item.service.participant_pricing_ranges || [];
						if (ranges.length > 0) {
							basePrice = getParticipantRangePrice(ranges, participantCount);
						}
						totalPrice = basePrice + calculatedOptionsPrice;
					} else if (priceMode === "per_equipment_unit") {
						const unitCapacity =
							item.service.equipment_requirements?.[0]?.equipment?.capacity_per_unit ||
							participantCount ||
							1;
						const unitsUsed = Math.max(1, Math.ceil(participantCount / unitCapacity));
						totalPrice = basePrice * unitsUsed + calculatedOptionsPrice;
					} else {
						totalPrice = basePrice * participantCount + calculatedOptionsPrice;
					}
					return { ...item, selectedOptions, optionsPrice: calculatedOptionsPrice, totalPrice };
				}
				return item;
			})
		);
	};

	const getTotalPrice = () => {
		return bookingItems.reduce((total, item) => total + item.totalPrice, 0);
	};

	const getDiscountedTotal = () => {
		return Math.max(0, getTotalPrice() - discountAmount);
	};

	const validateDiscountCode = async () => {
		if (!discountCode.trim()) {
			setDiscountError("Veuillez saisir un code de réduction");
			return;
		}

		setDiscountLoading(true);
		setDiscountError("");

		try {
			const response = await fetch("/api/discount-coupons/validate", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					code: discountCode,
					serviceId: bookingItems[0]?.service?.id,
					totalAmount: getTotalPrice(),
				}),
			});

			const result = await response.json();

			if (result.valid) {
				setDiscountAmount(result.discountAmount || 0);
				setAppliedCoupon(result.coupon);
				setDiscountError("");
			} else {
				setDiscountError(result.error || "Code de réduction invalide");
				setDiscountAmount(0);
				setAppliedCoupon(null);
			}
		} catch (error) {
			console.error("Error validating discount code:", error);
			setDiscountError("Erreur lors de la validation du code");
			setDiscountAmount(0);
			setAppliedCoupon(null);
		} finally {
			setDiscountLoading(false);
		}
	};

	const removeDiscountCode = () => {
		setDiscountCode("");
		setDiscountAmount(0);
		setDiscountError("");
		setAppliedCoupon(null);
	};

	const validateForm = (): boolean => {
		const errors: Partial<FormData> = {};

		if (!formData.firstName.trim()) errors.firstName = "Prénom requis";
		if (!formData.lastName.trim()) errors.lastName = "Nom requis";
		if (!formData.email.trim()) errors.email = "Email requis";
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = "Email invalide";
		if (!formData.phone.trim()) errors.phone = "Téléphone requis";

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) return;

		setIsLoading(true);

		try {
			// Create bookings for each booking item
			const bookingPromises = bookingItems.map(async (item) => {
				const bookingData = {
					serviceId: item.service.id,
					timeSlotId: item.timeSlot.id,
					tierParticipants: item.tierParticipants,
					customerInfo: {
						firstName: formData.firstName,
						lastName: formData.lastName,
						email: formData.email,
						phone: formData.phone,
						emergencyContactName: formData.emergencyContactName || undefined,
						emergencyContactPhone: formData.emergencyContactPhone || undefined,
					},
					specialRequests: formData.specialRequests || undefined,
					selectedOptions: item.selectedOptions || [],
					discountCode: appliedCoupon?.code || undefined,
				};

				const response = await fetch("/api/bookings", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(bookingData),
				});

				if (!response.ok) {
					const error = await response.json();
					// Use specific validation errors if available, otherwise use generic error
					const errorMessage =
						error.details && error.details.length > 0
							? error.details.join(", ")
							: error.error || "Failed to create booking";
					throw new Error(errorMessage);
				}

				return await response.json();
			});

			const results = await Promise.all(bookingPromises);

			debugLog("=== BOOKING COMPLETION DEBUG ===");
			debugLog("Booking results:", results);
			debugLog("First result:", results[0]);
			debugLog("First booking data:", results[0]?.data);

			// All bookings successful - show payment
			const firstBookingId = results[0]?.data?.reservationId;
			debugLog("Extracted reservation ID:", firstBookingId);

			setReservationId(firstBookingId);
			setCurrentStep(6); // Move to payment step
		} catch (error) {
			console.error("Booking error:", error);
			setIsLoading(false);
			alert(
				`Erreur lors de la création de la réservation: ${
					error instanceof Error ? error.message : "Erreur inconnue"
				}`
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handlePaymentSuccess = (paymentIntentId: string) => {
		console.log("=== PAYMENT SUCCESS DEBUG ===");
		console.log("requiresSecurityDeposit:", requiresSecurityDeposit);
		console.log("securityDepositAmount:", securityDepositAmount);
		console.log("selectedService:", selectedService);

		// Find the current service to double-check security deposit requirement
		const currentService = services.find((s) => s.id === selectedService);
		console.log("currentService security deposit fields:", {
			requires_security_deposit: currentService?.requires_security_deposit,
			security_deposit_amount: currentService?.security_deposit_amount,
		});

		// Check if security deposit is required
		if (requiresSecurityDeposit && securityDepositAmount > 0) {
			console.log("Redirecting to security deposit selection step");
			// Move to security deposit selection step
			setCurrentStep(6.5);
		} else {
			console.log("Skipping security deposit, going to confirmation");
			// Redirect to confirmation page
			if (typeof window !== "undefined") {
				window.location.href = `/reservation/confirmation?booking=${reservationId}&payment=${paymentIntentId}`;
			}
		}
	};

	// Security deposit handlers
	const handleSecurityDepositMethodSelected = (method: "authorization" | "manual") => {
		setSecurityDepositMethod(method);
		if (method === "authorization") {
			setCurrentStep(6.75); // Move to authorization form
		} else {
			// For manual method, go directly to confirmation
			if (typeof window !== "undefined") {
				window.location.href = `/reservation/confirmation?booking=${reservationId}`;
			}
		}
	};

	const handleSecurityDepositComplete = () => {
		// For manual method, go to confirmation
		if (typeof window !== "undefined") {
			window.location.href = `/reservation/confirmation?booking=${reservationId}`;
		}
	};

	const handleSecurityDepositSuccess = (paymentIntentId: string) => {
		setSecurityDepositPaymentIntentId(paymentIntentId);
		// Go to confirmation page
		if (typeof window !== "undefined") {
			window.location.href = `/reservation/confirmation?booking=${reservationId}&security_deposit=${paymentIntentId}`;
		}
	};

	const handleSecurityDepositError = (error: string) => {
		console.error("Security deposit error:", error);
		// Could show error message or allow retry
		alert(`Erreur lors de l'autorisation de caution: ${error}`);
	};

	const handleSecurityDepositCancel = () => {
		// Go back to security deposit selection
		setCurrentStep(6.5);
		setSecurityDepositMethod(null);
	};

	const handlePaymentError = (error: string) => {
		console.error("Payment error:", error);

		// Check if this might be an ad blocker issue
		if (error.includes("Failed to fetch") || error.includes("ERR_BLOCKED_BY_CLIENT")) {
			alert(
				`Erreur de réseau: ${error}\n\nSi vous utilisez un bloqueur de publicités, veuillez le désactiver temporairement. Votre paiement pourrait avoir été traité malgré cette erreur. Vérifiez vos emails ou contactez-nous si nécessaire.`
			);
		} else {
			alert(`Erreur de paiement: ${error}`);
		}
		// Stay on payment step to allow retry
	};

	const handlePaymentTypeSelect = (paymentType: PaymentType, amount: number) => {
		setSelectedPaymentType(paymentType);
		setPaymentAmount(amount);
	};

	const canProceedToNextStep = () => {
		switch (currentStep) {
			case 1:
				return selectedService !== null;
			case 2:
				return selectedDate && selectedTimeSlot; // Need date and time slot selected
			case 3:
				return tierParticipants.length > 0 && totalPrice > 0;
			case 4:
				return formData.firstName && formData.lastName && formData.email && formData.phone;
			case 5:
				return true; // Can proceed to payment
			case 6:
				return false; // Payment step - no next step
			default:
				return true;
		}
	};

	const formatDate = (dateString: string) => {
		// Parse the date string manually to avoid timezone issues
		const [year, month, day] = dateString.split("-").map(Number);
		const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
		return date.toLocaleDateString("fr-FR", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const sortedCategories = useMemo(() => {
		return [...categories].sort((a, b) => {
			const orderDiff = (a.display_order || 0) - (b.display_order || 0);
			if (orderDiff !== 0) {
				return orderDiff;
			}
			return (a.name || "").localeCompare(b.name || "");
		});
	}, [categories]);

	const selectedCategory = selectedCategoryId
		? categories.find((category) => category.id === selectedCategoryId) || null
		: null;

	const filteredServices = useMemo(() => {
		if (selectedCategoryId) {
			return services.filter((service) => service.category_id === selectedCategoryId);
		}
		return services;
	}, [services, selectedCategoryId]);

	const showServiceList =
		selectedCategoryId !== null || (!categoriesLoading && (categoriesError !== null || sortedCategories.length === 0));

	return (
		<div className="min-h-screen bg-gradient-to-b from-sky-50 to-emerald-50">
			{/* Header */}
			<header className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-50">
				<div className="container mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						<Link href="/" className="flex items-center space-x-3">
							<Image
								src="/images/logo-hd.png"
								alt="Soleil & Découverte"
								width={60}
								height={60}
								className="object-contain"
							/>
							<div>
								<h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
									Soleil & Découverte
								</h1>
								<p className="text-sm text-emerald-600">Réservation en ligne</p>
							</div>
						</Link>

						<Link href="/">
							<Button variant="ghost" size="sm">
								<X className="w-4 h-4 mr-2" />
								Fermer
							</Button>
						</Link>
					</div>
				</div>
			</header>

			{/* Progress Steps */}
			<div className="bg-white border-b">
				<div className="container mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						{steps.map((step, index) => (
							<div key={step.id} className="flex items-center">
								<div
									className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
										currentStep >= step.id
											? "bg-emerald-500 text-white"
											: "bg-gray-200 text-gray-500"
									}`}
								>
									{currentStep > step.id ? <Check className="w-4 h-4" /> : step.id}
								</div>
								<div className="ml-2 hidden sm:block">
									<div
										className={`text-sm font-medium ${
											currentStep >= step.id ? "text-emerald-600" : "text-gray-500"
										}`}
									>
										{step.title}
									</div>
									<div className="text-xs text-gray-400">{step.description}</div>
								</div>
								{index < steps.length - 1 && (
									<ChevronRight className="w-4 h-4 text-gray-300 mx-2 hidden sm:block" />
								)}
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="container mx-auto px-4 py-8">
				<div className={`mx-auto ${currentStep === 1 ? "w-full" : "max-w-4xl"}`}>
					<ClientOnlyWrapper>
						<AnimatePresence mode="wait">
							{/* Step 1: Service Selection */}
							{currentStep === 1 && (
								<motion.div
									key="step1"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">
											Choisissez vos excursions
										</h2>
										<p className="text-gray-600">
											Sélectionnez une ou plusieurs activités pour votre aventure
										</p>
									</div>

									{selectedCategory && (
										<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
											<div>
												<p className="text-xs uppercase tracking-wide text-emerald-600 font-semibold">Categorie</p>
												<h3 className="text-2xl font-bold text-gray-900">{selectedCategory.name}</h3>
												{selectedCategory.description && (
													<p className="text-gray-600 text-sm">{selectedCategory.description}</p>
												)}
											</div>
											<Button
												variant="outline"
												onClick={handleCategoryReset}
												className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
											>
												Voir les catégories
											</Button>
										</div>
									)}

									{!showServiceList && (
										<div className="mb-8">
											{categoriesLoading && (
												<div className="flex justify-center items-center py-12">
													<Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
													<span className="ml-2 text-emerald-600">Chargement des catégories...</span>
												</div>
											)}

											{categoriesError && (
												<div className="text-center py-8">
													<div className="text-red-600 mb-2">
														<span className="text-lg font-semibold">Erreur de chargement</span>
													</div>
													<p className="text-gray-600">{categoriesError}</p>
												</div>
											)}

											{!categoriesLoading && !categoriesError && sortedCategories.length === 0 && (
												<div className="text-center py-8">
													<div className="text-gray-600 mb-2">
														<span className="text-lg font-semibold">Aucune catégorie disponible</span>
													</div>
													<p className="text-gray-500">Ajoutez des catégories pour organiser vos activités.</p>
												</div>
											)}

											{!categoriesLoading && !categoriesError && sortedCategories.length > 0 && (
												<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
													{sortedCategories.map((category) => {
														const serviceCount = services.filter((service) => service.category_id === category.id).length;
														return (
															<button
																key={category.id}
																onClick={() => handleCategorySelect(category.id)}
																className="text-left h-full"
															>
																<CategoryCard
																	category={category}
																	serviceCount={serviceCount}
																	isCountLoading={servicesLoading}
																/>
															</button>
														);
													})}
												</div>
											)}
										</div>
									)}

									{showServiceList && (
										<>
											{categoriesError && (
												<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
													<p className="text-red-700 text-sm">{categoriesError}</p>
												</div>
											)}
											<div className="grid md:grid-cols-2 gap-6 mb-8">
												{servicesLoading ? (
													<div className="col-span-2 flex items-center justify-center py-12">
														<Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
														<span className="ml-3 text-emerald-600 font-medium">Chargement des activités...</span>
													</div>
												) : filteredServices.length === 0 ? (
													<div className="col-span-2 text-center py-12 text-gray-500">Aucune activité disponible dans cette catégorie.</div>
												) : (
													filteredServices.map((service) => (
														<motion.div
															key={service.id}
															whileHover={{ y: -5 }}
															whileTap={{ scale: 0.98 }}
														>
															<Card
																className={`cursor-pointer transition-all duration-300 ${
																	selectedService === service.id
																		? "ring-2 ring-emerald-500 bg-emerald-50"
																		: "hover:shadow-lg"
																}`}
																onClick={() => toggleService(service.id)}
															>
																<div className="relative">
																	<Image
																		src={service.image_url || "/placeholder.svg"}
																		alt={service.name}
																		width={400}
																		height={200}
																		className="w-full h-48 object-cover rounded-t-lg"
																	/>
																	{selectedService === service.id && (
																		<div className="absolute top-4 right-4 bg-emerald-500 text-white rounded-full p-2">
																			<Check className="w-4 h-4" />
																		</div>
																	)}
																	{preSelectedServiceId && preSelectedServiceId === service.id && (
																		<div className="absolute top-4 left-4 bg-orange-500 text-white rounded-full px-3 py-1 text-sm font-semibold">
																			Recommandé
																		</div>
																	)}
																	<div className="absolute bottom-4 left-4">
																		<Badge className="bg-white/90 text-emerald-700 font-semibold">
																			{service.max_age
																				? `${service.min_age}-${service.max_age} ans`
																				: service.min_age === 0 || service.min_age === null
																					? "Pour tous les âges"
																					: `À partir de ${service.min_age} ans`}
																		</Badge>
																	</div>
																</div>

																<CardContent className="p-6">
																	<h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
																	<p className="text-gray-600 mb-4 text-sm">{service.description}</p>

																	<div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
																		<div className="flex items-center">
																			<Clock className="w-4 h-4 mr-1" />
																			{Math.floor(service.duration_minutes / 60)}h
																			{service.duration_minutes % 60 > 0 ? ` ${service.duration_minutes % 60}min` : ""}
																		</div>
																		<div className="flex items-center">
																			<Users className="w-4 h-4 mr-1" />
																			{service.capacity}
																		</div>
																		<div className="flex items-center">
																			{service.pricing_model === "per_participant_range" && service.participant_pricing_ranges?.length
																				? service.participant_pricing_ranges[0].total_price
																				: service.base_price || service.pricing_tiers?.[0]?.price || 0}
																			€
																		</div>
																	</div>
																</CardContent>
															</Card>
														</motion.div>
													))
												)}
											</div>
										</>
									)}

									{/* Service Options Selection */}
									{showServiceList && selectedService && (
										<div className="mt-8">
											{(() => {
												const assignments = serviceAssignments[selectedService] || [];
												const requiredGroups = serviceRequiredGroups[selectedService] || [];
												return assignments.length > 0 ? (
													<Card className="p-6">
														<ServiceOptionsSelector
															assignments={assignments}
															selectedOptions={selectedOptions}
															participantCount={
																bookingItems.length > 0
																	? bookingItems[0].tierParticipants.reduce(
																			(sum, tp) => sum + tp.count,
																			0
																		)
																	: 1
															}
															requiredGroups={requiredGroups}
															showValidation={false}
															showValidationErrors={showOptionsValidation}
															onSelectionChange={(options: OptionSelection[]) => {
																setSelectedOptions(options);
																// Reset validation when user makes changes
																setShowOptionsValidation(false);
																// Update booking items if they exist
																if (bookingItems.length > 0) {
																	updateSelectedOptions(0, options, optionsPrice);
																}
															}}
															onPriceChange={(price: number) => {
																setOptionsPrice(price);
																// Update booking items if they exist
																if (bookingItems.length > 0) {
																	updateSelectedOptions(0, selectedOptions, price);
																}
															}}
														/>
													</Card>
												) : null;
											})()}
										</div>
									)}
								</motion.div>
							)}

							{/* Step 2: Date & Time Selection */}
							{currentStep === 2 && (
								<motion.div
									key="step2"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">Choisissez votre date</h2>
										<p className="text-gray-600">
											Sélectionnez une date puis vos créneaux horaires
										</p>
									</div>

									{/* Date Selection */}
									<Card className="mb-8">
										<CardHeader>
											<CardTitle className="flex items-center gap-2">
												<CalendarIcon className="w-5 h-5" />
												Sélectionnez une date
											</CardTitle>
										</CardHeader>
										<CardContent className="flex justify-center">
											<Calendar
												mode="single"
												selected={selectedDate}
												onSelect={setSelectedDate}
												startMonth={new Date()}
												endMonth={
													new Date(new Date().setFullYear(new Date().getFullYear() + 1))
												} // Un an à partir d'aujourd'hui
												disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} // Désactiver les dates passées
												className="rounded-md border"
											/>
										</CardContent>
									</Card>

									{/* Time Slot Selection */}
									{selectedDate && (
										<motion.div
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ duration: 0.3 }}
										>
											<div className="space-y-6">
												{selectedService &&
													(() => {
														const service = services.find((s) => s.id === selectedService);
														const slots = timeSlots || [];

														if (!service) return null;

														return (
															<Card key={selectedService}>
																<CardHeader>
																	<CardTitle className="flex items-center gap-2">
																		<Clock className="w-5 h-5" />
																		{service?.name}
																	</CardTitle>
																	<p className="text-sm text-gray-600">
																		{formatDate(
																			`${selectedDate.getFullYear()}-${String(
																				selectedDate.getMonth() + 1
																			).padStart(2, "0")}-${String(
																				selectedDate.getDate()
																			).padStart(2, "0")}`
																		)}{" "}
																		à Choisissez un créneau
																	</p>
																</CardHeader>
																<CardContent>
																	{timeSlotsLoading ? (
																		<div className="flex items-center justify-center py-8">
																			<div className="flex items-center gap-3">
																				<Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
																				<span className="text-gray-600">
																					Chargement des créneaux
																					disponibles...
																				</span>
																			</div>
																		</div>
																	) : slots.length === 0 ? (
																		<div className="text-center py-8">
																			<p className="text-gray-500">
																				Aucun créneau disponible pour cette date
																			</p>
																		</div>
																	) : (
																		<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
																			{slots.map((slot) => {
																				// Use timezone-safe time formatting
																				const slotTime = formatTimeFromISO(
																					slot.start_time
																				);
																				const timeSlotDisplay =
																					formatTimeRangeFromISO(
																						slot.start_time,
																						slot.end_time
																					);
																				const isSelected =
																					selectedTimeSlot === slotTime;

																				const totalParticipants =
																					tierParticipants.reduce(
																						(sum, tp) => sum + tp.count,
																						0
																					);
																				const hasInsufficientCapacity =
																					totalParticipants > 0 &&
																					slot.available_capacity <
																						totalParticipants;
																				const isSlotDisabled =
																					!slot.is_available ||
																					hasInsufficientCapacity;

																				return (
																					<Button
																						key={`${slot.start_time}-${slot.available_capacity}`}
																						variant={
																							isSelected
																								? "outline"
																								: "outline"
																						}
																						disabled={isSlotDisabled}
																						className={`h-auto p-3 flex flex-col transition-all duration-200 ${
																							isSlotDisabled
																								? "opacity-50 cursor-not-allowed"
																								: "hover:bg-emerald-50 hover:border-emerald-300 hover:shadow-sm" +
																									(isSelected
																										? " bg-emerald-50 border-emerald-300 shadow-sm"
																										: "")
																						} text-black`}
																						onClick={() =>
																							!isSlotDisabled &&
																							addBookingItem(slotTime)
																						}
																					>
																						<div className="text-lg font-bold text-black">
																							{timeSlotDisplay}
																						</div>
																						<div className="text-xs text-gray-500">
																							{!slot.is_available ? (
																								"Complet"
																							) : hasInsufficientCapacity ? (
																								<span className="text-red-600">
																									{
																										slot.available_capacity
																									}{" "}
																									places seulement
																								</span>
																							) : (
																								`${slot.available_capacity} places`
																							)}
																						</div>
																					</Button>
																				);
																			})}
																		</div>
																	)}
																</CardContent>
															</Card>
														);
													})()}
											</div>
										</motion.div>
									)}

									{/* Selected Bookings */}
								</motion.div>
							)}

							{/* Step 3: Participants */}
							{currentStep === 3 && (
								<motion.div
									key="step3"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">
											Nombre de participants
										</h2>
										<p className="text-gray-600">
											Sélectionnez le nombre et le type de participants
										</p>
									</div>

									{bookingItems.length > 0 && (
										<div className="space-y-6">
											{/* Service Info */}
											<Card>
												<CardContent className="p-6">
													<div className="flex items-center gap-4 mb-4">
														<Image
															src={
																bookingItems[0].service.image_url || "/placeholder.svg"
															}
															alt={bookingItems[0].service.name}
															width={80}
															height={80}
															className="rounded-lg object-cover"
														/>
														<div className="flex-1">
															<h3 className="text-lg font-semibold">
																{bookingItems[0].service.name}
															</h3>
															<p className="text-sm text-gray-600">
																{formatDate(bookingItems[0].date)} à{" "}
																{formatTimeFromISO(bookingItems[0].timeSlot.start_time)}
															</p>
														</div>
													</div>
												</CardContent>
											</Card>

											{/* Pricing Calculator */}
											<PricingCalculator
												pricingTiers={(bookingItems[0].service.pricing_tiers || []).map(
													(tier) => ({
														...tier,
														min_age: tier.min_age || 0,
														max_age: tier.max_age || null,
													})
												)}
												maxParticipants={
													bookingItems[0].timeSlot.available_capacity ||
													bookingItems[0].service.max_participants ||
													10
												}
												basePrice={bookingItems[0].service.base_price || 0}
												priceMode={
													bookingItems[0].service.pricing_model === "per_equipment_unit"
														? "per_unit"
														: bookingItems[0].service.pricing_model === "fixed_total"
															? "fixed_total"
															: bookingItems[0].service.pricing_model ===
																  "per_participant_range"
																? "per_participant_range"
																: "per_participant"
												}
												participantRanges={
													bookingItems[0].service.participant_pricing_ranges || []
												}
												unitCapacity={
													bookingItems[0].service.equipment_requirements?.[0]?.equipment
														?.capacity_per_unit
												}
												unitLabel={
													bookingItems[0].service.pricing_model === "per_equipment_unit" ||
													bookingItems[0].service.pricing_model === "per_participant_range"
														? "bateau"
														: undefined
												}
												initialTierParticipants={tierParticipants}
												onPriceChange={handlePriceChange}
											/>
										</div>
									)}
								</motion.div>
							)}

							{/* Step 4: Personal Information */}
							{currentStep === 4 && (
								<motion.div
									key="step4"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">Vos informations</h2>
										<p className="text-gray-600">Renseignez vos coordonnées pour la réservation</p>
									</div>

									<Card>
										<CardContent className="p-6">
											<div className="grid md:grid-cols-2 gap-6">
												<div>
													<Label htmlFor="firstName">Prénom *</Label>
													<Input
														id="firstName"
														value={formData.firstName}
														onChange={(e) =>
															setFormData((prev) => ({
																...prev,
																firstName: e.target.value,
															}))
														}
														className={formErrors.firstName ? "border-red-500" : ""}
													/>
													{formErrors.firstName && (
														<p className="text-red-500 text-sm mt-1">
															{formErrors.firstName}
														</p>
													)}
												</div>

												<div>
													<Label htmlFor="lastName">Nom *</Label>
													<Input
														id="lastName"
														value={formData.lastName}
														onChange={(e) =>
															setFormData((prev) => ({
																...prev,
																lastName: e.target.value,
															}))
														}
														className={formErrors.lastName ? "border-red-500" : ""}
													/>
													{formErrors.lastName && (
														<p className="text-red-500 text-sm mt-1">
															{formErrors.lastName}
														</p>
													)}
												</div>

												<div>
													<Label htmlFor="email">Email *</Label>
													<Input
														id="email"
														type="email"
														value={formData.email}
														onChange={(e) =>
															setFormData((prev) => ({ ...prev, email: e.target.value }))
														}
														className={formErrors.email ? "border-red-500" : ""}
													/>
													{formErrors.email && (
														<p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
													)}
												</div>

												<div>
													<Label htmlFor="phone">Téléphone *</Label>
													<Input
														id="phone"
														value={formData.phone}
														onChange={(e) =>
															setFormData((prev) => ({ ...prev, phone: e.target.value }))
														}
														className={formErrors.phone ? "border-red-500" : ""}
														placeholder="+33 6 XX XX XX XX"
													/>
													{formErrors.phone && (
														<p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
													)}
												</div>

												<div className="md:col-span-2">
													<Label htmlFor="specialRequests">
														Demandes spéciales (optionnel)
													</Label>
													<Textarea
														id="specialRequests"
														value={formData.specialRequests}
														onChange={(e) =>
															setFormData((prev) => ({
																...prev,
																specialRequests: e.target.value,
															}))
														}
														placeholder="Allergies, besoins particuliers, etc."
														rows={3}
													/>
												</div>
											</div>
										</CardContent>
									</Card>
								</motion.div>
							)}

							{/* Step 5: Confirmation */}
							{currentStep === 5 && (
								<motion.div
									key="step5"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">
											Confirmation de réservation
										</h2>
										<p className="text-gray-600">
											Vérifiez vos informations avant de procéder au paiement
										</p>
									</div>

									{/* Order Summary */}
									<Card className="mb-8">
										<CardHeader>
											<CardTitle className="text-xl">Récapitulatif de commande</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="space-y-6">
												{bookingItems.map((item, index) => (
													<div key={index} className="space-y-3">
														{/* Service Name */}
														<div>
															<h3 className="text-lg font-semibold text-gray-900">
																{item.service.name}
															</h3>
														</div>

														{/* Date and Time */}
														<div className="text-sm text-gray-600">
															{formatDate(item.date)} à{" "}
															{formatTimeRangeFromISO(
																item.timeSlot.start_time,
																item.timeSlot.end_time
															)}
														</div>

														{/* Selected Options */}
														{item.selectedOptions && item.selectedOptions.length > 0 && (
															<div className="space-y-2">
																<h4 className="text-sm font-medium text-gray-700">
																	Options sélectionnées:
																</h4>
																<div className="space-y-1">
																	{item.selectedOptions.map((selection) => {
																		// Find the option in the service's optionsV2 assignments
																		// Find option details for display
																		let option: ServiceOption | undefined;
																		const assignments =
																			serviceAssignments[item.service.id] || [];
																		const assignment = assignments.find(
																			(a) => a.option?.id === selection.optionId
																		);
																		if (assignment?.option) {
																			option = assignment.option;
																		}

																		return option ? (
																			<div
																				key={selection.optionId}
																				className="flex justify-between text-sm"
																			>
																				<span className="text-gray-600">
																					{option.name}
																					{option.basePrice > 0 &&
																					option.perParticipant
																						? ` (${item.tierParticipants.reduce(
																								(sum, tp) =>
																									sum + tp.count,
																								0
																							)} × ${option.basePrice.toFixed(2)}€)`
																						: ""}
																				</span>
																				<span className="text-gray-900">
																					{option.basePrice > 0
																						? option.perParticipant
																							? `${(
																									option.basePrice *
																									item.tierParticipants.reduce(
																										(sum, tp) =>
																											sum +
																											tp.count,
																										0
																									)
																								).toFixed(2)}€`
																							: `${option.basePrice.toFixed(2)}€`
																						: option.quoteBased
																							? "Sur devis"
																							: "Gratuit"}
																				</span>
																			</div>
																		) : null;
																	})}
																</div>
															</div>
														)}

														{/* Participants and Price */}
														<div className="space-y-2">
															{/* Participants Breakdown */}
															{item.service.pricing_model === "fixed_total" ? (
																<div className="flex justify-between">
																	<span className="text-sm text-gray-600">
																		Prix fixe pour{" "}
																		{item.tierParticipants.reduce(
																			(sum, tp) => sum + tp.count,
																			0
																		)}{" "}
																		participant
																		{item.tierParticipants.reduce(
																			(sum, tp) => sum + tp.count,
																			0
																		) > 1
																			? "s"
																			: ""}
																	</span>
																	<span className="text-sm text-gray-600">
																		{(item.totalPrice - item.optionsPrice).toFixed(
																			2
																		)}
																		€
																	</span>
																</div>
															) : item.service.pricing_model ===
															  "per_participant_range" ? (
																(() => {
																	const participantCount =
																		item.tierParticipants.reduce(
																			(sum, tp) => sum + tp.count,
																			0
																		);
																	const activeRange = findParticipantRange(
																		item.service.participant_pricing_ranges || [],
																		participantCount
																	);
																	const rangeLabel = activeRange
																		? formatParticipantRangeLabel(activeRange)
																		: `${participantCount} participant${
																				participantCount > 1 ? "s" : ""
																			}`;
																	const unitCapacity =
																		item.service.equipment_requirements?.[0]?.equipment
																			?.capacity_per_unit || 0;
																	const unitsUsed =
																		unitCapacity > 0
																			? Math.max(1, Math.ceil(participantCount / unitCapacity))
																			: null;

																	return (
																		<>
																			<div className="flex justify-between">
																				<span className="text-sm text-gray-600">
																					Tranche {rangeLabel}
																				</span>
																				<span className="text-sm text-gray-600">
																					{(
																						item.totalPrice - item.optionsPrice
																					).toFixed(2)}
																					€
																				</span>
																			</div>
																			{unitsUsed !== null && (
																				<div className="text-sm text-emerald-700">
																					Bateaux réservés: {unitsUsed}
																				</div>
																			)}
																		</>
																	);
																})()
															) : (
																<>
																	{item.service.pricing_model ===
																		"per_equipment_unit" && (
																		<div className="flex justify-between">
																			<span className="text-sm text-gray-600">
																				{Math.max(
																					1,
																					Math.ceil(
																						item.tierParticipants.reduce(
																							(sum, tp) => sum + tp.count,
																							0
																						) /
																							(item.service
																								.equipment_requirements?.[0]
																								?.equipment
																								?.capacity_per_unit ||
																								1)
																					)
																				)}{" "}
																				× bateau (
																				{(item.service.base_price || 0).toFixed(
																					2
																				)}
																				€)
																			</span>
																			<span className="text-sm text-gray-600">
																				{(
																					item.totalPrice - item.optionsPrice
																				).toFixed(2)}
																				€
																			</span>
																		</div>
																	)}

																	{/* Show tier participant breakdown */}
																	{item.service.pricing_model !==
																		"per_equipment_unit" &&
																		item.tierParticipants.map((tierParticipant) => {
																			// Find the tier details
																			const tier =
																				item.service.pricing_tiers?.find(
																					(t) =>
																						t.id === tierParticipant.tierId
																				);
																			const tierName =
																				tier?.tier_name || "Standard";
																			const tierPrice =
																				tier?.price ||
																				item.service.base_price ||
																				0;

																			return (
																				<div
																					key={tierParticipant.tierId}
																					className="flex justify-between"
																				>
																					<span className="text-sm text-gray-600">
																						{tierParticipant.count} ×{" "}
																						{tierName} (
																						{tierPrice.toFixed(2)}€)
																					</span>
																					<span className="text-sm text-gray-600">
																						{(
																							tierParticipant.count *
																							tierPrice
																						).toFixed(2)}
																						€
																					</span>
																				</div>
																			);
																		})}
																</>
															)}

															{/* Service Total (removed redundant options line and individual total) */}
														</div>
													</div>
												))}

												<Separator className="my-6" />

												{/* Discount Coupon Section */}
												<div className="space-y-4 rounded-lg border border-emerald-200/80 bg-emerald-50/40 p-4">
													<div className="space-y-1">
														<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
															<Tag className="h-4 w-4 text-emerald-600" />
															Code de réduction
														</h3>
														<p className="text-sm text-gray-600">
															Vous avez un code promo ? Appliquez-le avant le paiement.
														</p>
													</div>

													{!appliedCoupon ? (
														<div className="space-y-3">
															<div className="flex gap-2">
																<Input
																	type="text"
																	placeholder="Entrez votre code de réduction"
																	value={discountCode}
																	onChange={(e) =>
																		setDiscountCode(e.target.value.toUpperCase())
																	}
																	className="flex-1 border-emerald-200 focus-visible:border-emerald-400 focus-visible:ring-emerald-500/30"
																	disabled={discountLoading}
																/>
																<Button
																	onClick={validateDiscountCode}
																	disabled={discountLoading || !discountCode.trim()}
																	variant="outline"
																	size="sm"
																	className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
																>
																	{discountLoading ? (
																		<Loader2 className="h-4 w-4 animate-spin" />
																	) : (
																		"Appliquer"
																	)}
																</Button>
															</div>
															{discountError && (
																<p className="text-sm text-red-600">{discountError}</p>
															)}
														</div>
													) : (
														<div className="bg-green-50 border border-green-200 rounded-lg p-4">
															<div className="flex items-center justify-between">
																<div>
																	<p className="font-medium text-green-800">
																		Code appliqué: {appliedCoupon.code}
																	</p>
																	{appliedCoupon.description && (
																		<p className="text-sm text-green-600">
																			{appliedCoupon.description}
																		</p>
																	)}
																	<p className="text-sm text-green-600">
																		Réduction: -{discountAmount.toFixed(2)}€
																	</p>
																</div>
																<Button
																	onClick={removeDiscountCode}
																	variant="ghost"
																	size="sm"
																	className="text-green-700 hover:text-green-900"
																>
																	<X className="h-4 w-4" />
																</Button>
															</div>
														</div>
													)}
												</div>

												<Separator className="my-6" />

												{/* Pricing Breakdown */}
												{discountAmount > 0 && (
													<div className="space-y-2">
														<div className="flex justify-between items-center text-gray-600">
															<span>Sous-total</span>
															<span>{getTotalPrice().toFixed(2)}€</span>
														</div>
														<div className="flex justify-between items-center text-green-600">
															<span>Réduction</span>
															<span>-{discountAmount.toFixed(2)}€</span>
														</div>
														<Separator className="my-2" />
													</div>
												)}

												{/* Total */}
												<div className="flex justify-between items-center">
													<span className="text-xl font-semibold text-gray-900">Total</span>
													<span className="text-2xl font-bold text-emerald-600">
														{getDiscountedTotal().toFixed(2)}€
													</span>
												</div>

												<Separator className="my-6" />

												{/* Payment Info */}
												<div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
													<div className="space-y-2 text-sm text-emerald-800">
														<div className="flex items-center gap-2">
															<div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
															<span>Paiement sécurisé par Stripe</span>
														</div>
														<div className="flex items-center gap-2">
															<div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
															<span>Confirmation immédiate par email</span>
														</div>
														<div className="flex items-center gap-2">
															<div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
															<span>Support client 7j/7</span>
														</div>
													</div>
												</div>
											</div>
										</CardContent>
									</Card>

									{/* Payment Selection */}
									<DepositSelection
										totalAmount={getDiscountedTotal()}
										onPaymentTypeSelect={handlePaymentTypeSelect}
										selectedPaymentType={selectedPaymentType}
									/>
								</motion.div>
							)}

							{/* Step 6: Payment */}
							{currentStep === 6 && reservationId && (
								<motion.div
									key="step6"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">Paiement sécurisé</h2>
										<p className="text-gray-600">Finalisez votre réservation avec le paiement</p>
									</div>

									{/* Payment Form */}
									<PaymentWrapper
										reservationId={reservationId}
										amount={paymentAmount || getDiscountedTotal()}
										paymentType={selectedPaymentType as PaymentType}
										onSuccess={handlePaymentSuccess}
										onError={handlePaymentError}
									/>
								</motion.div>
							)}

							{/* Step 6.5: Security Deposit Selection */}
							{currentStep === 6.5 && reservationId && requiresSecurityDeposit && (
								<motion.div
									key="step6.5"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">Caution d'équipement</h2>
										<p className="text-gray-600">
											Choisissez comment gérer la caution de {effectiveSecurityDepositAmount}€
										</p>
									</div>

									<SecurityDepositSelector
										reservationId={reservationId}
										securityDepositAmount={effectiveSecurityDepositAmount}
										onMethodSelected={handleSecurityDepositMethodSelected}
										onComplete={handleSecurityDepositComplete}
										disabled={effectiveSecurityDepositAmount <= 0}
									/>
								</motion.div>
							)}

							{/* Step 6.75: Security Deposit Authorization */}
							{currentStep === 6.75 && reservationId && securityDepositMethod === "authorization" && (
								<motion.div
									key="step6.75"
									initial={{ opacity: 0, x: 50 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -50 }}
									transition={{ duration: 0.3 }}
								>
									<div className="text-center mb-8">
										<h2 className="text-3xl font-bold text-gray-900 mb-4">
											Autorisation de caution
										</h2>
										<p className="text-gray-600">
											Autorisez la caution de {effectiveSecurityDepositAmount}€ sur votre carte
										</p>
									</div>

									<SecurityDepositAuthorizationForm
										reservationId={reservationId}
										amount={effectiveSecurityDepositAmount}
										onSuccess={handleSecurityDepositSuccess}
										onError={handleSecurityDepositError}
										onCancel={handleSecurityDepositCancel}
									/>
								</motion.div>
							)}
						</AnimatePresence>
					</ClientOnlyWrapper>

					{/* Navigation Buttons */}
					<div className="flex justify-between items-center mt-8 pt-8 border-t">
						<Button
							variant="outline"
							onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
							disabled={currentStep === 1 || currentStep === 6}
							className="flex items-center gap-2"
						>
							<ChevronLeft className="w-4 h-4" />
							Précédent
						</Button>

						{currentStep < 5 ? (
							<Button
								onClick={() => {
									// If moving from step 1, validate options first
									if (currentStep === 1 && selectedService) {
										const assignments = serviceAssignments[selectedService] || [];
										const requiredGroups = serviceRequiredGroups[selectedService] || [];

										if (assignments.length > 0 && requiredGroups.length > 0) {
											const validation = validateOptionSelections(
												assignments,
												selectedOptions,
												requiredGroups
											);
											if (!validation.isValid) {
												setShowOptionsValidation(true);
												return; // Don't proceed if validation fails
											}
										}
									}

									// If moving from step 2 to 3, ensure booking item is created
									if (currentStep === 2 && selectedTimeSlot && bookingItems.length === 0) {
										addBookingItem(selectedTimeSlot);
									}
									setCurrentStep((prev) => prev + 1);
								}}
								disabled={!canProceedToNextStep()}
								className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600"
							>
								Suivant
								<ChevronRight className="w-4 h-4" />
							</Button>
						) : currentStep === 5 ? (
							<Button
								onClick={handleSubmit}
								disabled={isLoading || !canProceedToNextStep()}
								className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600"
							>
								{isLoading ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Traitement...
									</>
								) : (
									<>
										Procéder au paiement
										<CreditCard className="w-4 h-4" />
									</>
								)}
							</Button>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
}
